import React from "react";
import { ArrowRight, Play, Wifi, ShieldCheck, Globe2 } from "lucide-react";
import PhoneFrame from "./PhoneFrame";
import { AppStoreBadges } from "./AppStoreBadges";
import { LANDING } from "@/constants/testIds";
import { MOCKUPS } from "@/constants/mockups";
import { WEB_REGISTER_URL } from "@/constants/site";
import { captureEvent } from "@/lib/analytics";

export const Hero = () => {
  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-[#F7FAF9]"
      aria-label="Shopbook — retail clarity across web and mobile"
    >
      {/* Background art */}
      <div className="absolute inset-0 bg-sunburst pointer-events-none" />
      <div className="absolute -top-24 -left-24 h-[460px] w-[460px] rounded-full bg-[#0E6B6B]/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 right-0 h-[420px] w-[420px] rounded-full bg-[#D96C4A]/10 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-adinkra opacity-[0.07] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-10 sm:px-6 sm:pb-20 sm:pt-16 lg:px-12 lg:pb-28 lg:pt-24">
        <div className="grid items-center gap-10 sm:gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left copy */}
          <div className="lg:col-span-7 animate-fade-up">
            <div className="mb-5 inline-flex max-w-full items-center gap-2 rounded-full border border-[#0E6B6B]/15 bg-white/75 px-3 py-2 text-[11px] font-semibold leading-4 tracking-wide text-[#0E6B6B] shadow-sm backdrop-blur sm:mb-6 sm:text-xs">
              <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#0E6B6B]" />
              <span>One operating record for retail · Web + mobile</span>
            </div>

            <h1 className="max-w-[18ch] font-display text-[2.35rem] font-semibold leading-[1.07] tracking-[-0.035em] text-[#0A2424] sm:max-w-none sm:text-5xl lg:text-6xl">
              Know what happened in your shop today. <span className="mt-1 block italic font-medium text-[#0E6B6B] sm:mt-0 sm:inline">Know what to do next.</span>
            </h1>

            <p className="mt-5 max-w-2xl text-[16px] leading-7 text-[#3a4a4a] md:text-lg">
              Shopbook turns every sale, stock change, customer balance, expense, and staff action into one reliable operating picture—captured on mobile, understood on the web.
            </p>

            <div className="mt-7 flex flex-col items-stretch gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href={WEB_REGISTER_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={LANDING.heroGetStarted}
                onClick={() => captureEvent("conversion_cta_clicked", { placement: "hero", destination: "web_register" })}
                className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#0E6B6B] px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-[#0E6B6B]/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#138282] sm:min-h-0 sm:w-auto sm:rounded-full"
              >
                Start my free trial <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#how-it-works"
                data-testid={LANDING.heroHowItWorks}
                onClick={() => captureEvent("product_demo_clicked", { placement: "hero" })}
                className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl border border-[#0E6B6B]/20 bg-white px-6 py-3.5 text-base font-semibold text-[#0E6B6B] transition-all hover:bg-[#0E6B6B]/5 sm:min-h-0 sm:w-auto sm:rounded-full"
              >
                <Play className="h-4 w-4" /> See the product
              </a>
            </div>

            <div className="mt-6 sm:mt-8">
              <AppStoreBadges
                playId={LANDING.heroPlayBadge}
                storeId={LANDING.heroAppStoreBadge}
                showAvailabilityNote
              />
            </div>

            <div className="mt-7 grid grid-cols-1 gap-3 text-sm text-[#3a4a4a] sm:mt-10 sm:grid-cols-3 sm:gap-x-5 lg:flex lg:flex-wrap lg:items-center lg:gap-x-8">
              <span className="inline-flex items-center gap-2.5">
                <Wifi className="h-4 w-4 shrink-0 text-[#0E6B6B]" /> Core mobile work continues offline
              </span>
              <span className="inline-flex items-center gap-2.5">
                <ShieldCheck className="h-4 w-4 shrink-0 text-[#0E6B6B]" /> No automatic trial charge
              </span>
              <span className="inline-flex items-center gap-2.5">
                <Globe2 className="h-4 w-4 shrink-0 text-[#0E6B6B]" /> 9 languages
              </span>
            </div>
          </div>

          {/* Right mockups */}
          <div className="relative mx-auto h-[480px] w-full max-w-[340px] sm:h-[620px] sm:max-w-none lg:col-span-5">
            {/* Decorative arc */}
            <svg
              className="absolute -top-6 left-1/2 -translate-x-1/2 w-[110%] opacity-30"
              viewBox="0 0 600 600"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="300" cy="300" r="260" stroke="#0E6B6B" strokeDasharray="2 6" />
              <circle cx="300" cy="300" r="200" stroke="#D96C4A" strokeDasharray="2 8" />
            </svg>

            {/* Phone stack */}
            <div
              className="absolute left-2 sm:left-6 top-12 w-[188px] sm:w-[214px] animate-float-slow-delay"
              style={{ "--tilt": "-6deg", transform: "rotate(-6deg)" }}
            >
              <PhoneFrame src={MOCKUPS.login} alt="Shopbook secure phone login" />
            </div>
            <div
              className="absolute right-2 sm:right-0 top-0 w-[210px] sm:w-[248px] z-10 animate-float-slow"
              style={{ "--tilt": "5deg", transform: "rotate(5deg)" }}
            >
              <PhoneFrame src={MOCKUPS.dashboard} alt="Shopbook dashboard with sales trend" />
            </div>
            <div
              className="absolute left-14 sm:left-24 bottom-1 sm:bottom-0 w-[182px] sm:w-[206px] z-20 animate-float-slow"
              style={{ "--tilt": "-2deg", transform: "rotate(-2deg)" }}
            >
              <PhoneFrame src={MOCKUPS.sales} alt="Shopbook sales screen" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
